import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, CalendarDays, Users, Scissors, Sparkles, Settings as SettingsIcon, LogOut, Menu, X, UserCog, Inbox, LifeBuoy, BarChart3, Building2, Clock,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SupportDialog from "@/components/SupportDialog";
import BusinessSwitcher from "@/components/BusinessSwitcher";
import AdminImpersonationBanner from "@/components/AdminImpersonationBanner";
import { api } from "@/lib/api";

const ALL_NAV = [
  { to: "/dashboard", label: "Oversikt", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["owner", "staff", "leder"] },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "nav-calendar", roles: ["owner", "staff", "leder"] },
  { to: "/kunder", label: "Kunder", icon: Users, testid: "nav-customers", roles: ["owner", "staff", "leder"], badge: "customers" },
  // Timelister (P1) — visible to all in-business roles whose business is
  // Premium, plus the public Nordlys demo (where the endpoint layer
  // forces read-only). Hidden for Basic/Pro plans.
  { to: "/timelister", label: "Timelister", icon: Clock, testid: "nav-timelister", roles: ["owner", "staff", "leder"], premiumOrDemo: true },
  { to: "/tjenester", label: "Tjenester", icon: Scissors, testid: "nav-services", roles: ["owner", "staff", "leder"] },
  { to: "/ai-meldinger", label: "AI Meldinger", icon: Sparkles, testid: "nav-ai", roles: ["owner", "staff", "leder"] },
  { to: "/rapporter", label: "Rapporter", icon: BarChart3, testid: "nav-reports", roles: ["owner"] },
  { to: "/personal", label: "Personal", icon: UserCog, testid: "nav-staff", roles: ["owner"] },
  { to: "/innstillinger", label: "Innstillinger", icon: SettingsIcon, testid: "nav-settings", roles: ["owner"] },
  { to: "/admin/leads", label: "Forespørsler", icon: Inbox, testid: "nav-leads", roles: ["owner"], platformAdminOnly: true, badge: "leads" },
  { to: "/admin/bedrifter", label: "Bedrifter", icon: Building2, testid: "nav-bedrifter", roles: ["owner"], platformAdminOnly: true },
];

export default function AppLayout() {
  const { user, logout, isPlatformAdmin, adminBusinessId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [bizName, setBizName] = useState("");
  const [bizPlan, setBizPlan] = useState("");
  const [bizSlug, setBizSlug] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  // Sidebar notification badges. Reuses existing endpoints so the counts
  // match what each user can *already* see: `/customers/follow-up`
  // returns exactly the "Kunder å følge opp" list (with `count`), and
  // `/contact/requests?status=new` returns the "Ny"-bucket that drives
  // the Forespørsler-page's "Ny"-chip. No new criteria or permission
  // rules — endpoints that return 402/403 simply hide the badge.
  const [badges, setBadges] = useState({ customers: 0, leads: 0 });

  // Reset window scroll (both Y AND X) on every in-app route change. iOS
  // Safari sometimes persists horizontal scroll position across route
  // transitions — even with `overflow-x: hidden` on body — and the user
  // lands on the new page with the viewport scrolled ~30-50px to the
  // right. That reads as "the page is offset / cropped on the left".
  // Forcing window.scrollTo(0, 0) immediately after navigation kills
  // that bug at the source. Honours `scroll-behavior: auto` so it isn't
  // animated; this is a layout reset, not a UX scroll.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setOpen(false);  // also close the mobile nav drawer on route change
  }, [location.pathname]);

  // Remember the current business name (honours the admin-impersonation
  // header automatically) so the support mailto can prefill subject/body.
  useEffect(() => {
    let mounted = true;
    api.get("/business")
      .then((r) => {
        if (!mounted) return;
        setBizName(r.data?.name || "");
        setBizPlan((r.data?.plan || "").toLowerCase());
        setBizSlug(r.data?.slug || "");
      })
      .catch(() => { if (mounted) { setBizName(""); setBizPlan(""); setBizSlug(""); } });
    return () => { mounted = false; };
  }, [user?.id]);

  // Build a mailto: link to ClientFlow support, prefilled with the business
  // name, the logged-in user's email, and the current page URL. Visible to
  // both Leder and Ansatt — internal help, not customer-facing.
  const supportHref = useMemo(() => {
    const business = bizName || "ClientFlow-kunde";
    const subject = `Support – ${business}`;
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const body = [
      "Hei ClientFlow support,",
      "",
      "Beskriv problemet kort her:",
      "",
      "",
      "— — — — — — — — — — — — — — — — —",
      `Bedrift: ${business}`,
      `Bruker:  ${user?.email || ""}`,
      `Side:    ${pageUrl}`,
    ].join("\n");
    return `mailto:support@clientflow.no?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [bizName, user?.email]);

  // Platform-admin nav items (e.g. Forespørsler) belong to ClientFlow HQ
  // only. When the Super Admin is impersonating a customer business via the
  // switcher (`adminBusinessId` set), hide them — they don't apply to the
  // customer context and seeing them under a salon's nav is confusing.
  const NAV = ALL_NAV
    .filter((n) => !user?.role || n.roles.includes(user.role))
    .filter((n) => !n.platformAdminOnly || (isPlatformAdmin && !adminBusinessId))
    .filter((n) => !n.premiumOrDemo || bizPlan === "premium" || bizSlug === "nordlys-frisor");

  // Poll sidebar-badge counts every 60 s (and immediately on nav-visibility
  // changes). We only hit endpoints whose nav item is actually visible to
  // this user, and any non-200 response silently zeros that badge — so
  // Basic-plan 402 on `/customers/follow-up` or a lack of platform-admin
  // access on `/contact/requests` both correctly render "no badge".
  const showCustomersBadge = NAV.some((n) => n.badge === "customers");
  const showLeadsBadge = NAV.some((n) => n.badge === "leads");
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [cust, leads] = await Promise.all([
        showCustomersBadge
          ? api.get("/customers/follow-up").then((r) => r.data?.count ?? 0).catch(() => 0)
          : Promise.resolve(0),
        showLeadsBadge
          ? api.get("/contact/requests", { params: { status: "new", limit: 1000 } })
              .then((r) => r.data?.count ?? 0).catch(() => 0)
          : Promise.resolve(0),
      ]);
      if (!cancelled) setBadges({ customers: cust, leads: leads });
    };
    load();
    const id = setInterval(load, 60_000);
    // Allow child pages (e.g. Forespørsler) to force an immediate badge
    // refresh after a mutation without waiting for the 60 s poll. Any
    // page can `window.dispatchEvent(new Event("cf:sidebar-badges-refresh"))`.
    const onExternalRefresh = () => { load(); };
    window.addEventListener("cf:sidebar-badges-refresh", onExternalRefresh);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("cf:sidebar-badges-refresh", onExternalRefresh);
    };
  }, [showCustomersBadge, showLeadsBadge, user?.id, adminBusinessId]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
      isActive
        ? "bg-primary text-primary-foreground"
        : "text-foreground/70 hover:bg-accent hover:text-foreground"
    }`;

  // Render a compact red pill when count > 0. Caps at "99+" so the number
  // never breaks the link's flex layout on narrow mobile sidebars.
  const renderBadge = (key) => {
    const n = badges[key] || 0;
    if (n <= 0) return null;
    const label = n > 99 ? "99+" : String(n);
    return (
      <span
        className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none"
        data-testid={`nav-badge-${key}`}
        aria-label={`${n} varsler`}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* App routes are behind auth — always tell crawlers to stay out. */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-50 w-full min-w-0 glass-nav border-b border-border safe-pt">
        <div className="w-full min-w-0 flex items-center px-4 h-14 safe-px">
          <Link to="/dashboard" className="flex items-center min-w-0" data-testid="app-logo" aria-label="ClientFlow">
            <LogoMark className="w-8 h-8 shrink-0" />
            <span className="ml-2 font-heading font-bold text-lg tracking-tight text-foreground truncate">ClientFlow</span>
          </Link>
          {/* Right-side cluster.
              Real-device audit (iPhone 12 Pro Max, iOS 18.7 Safari 26.5,
              vw=428) caught the original structure pushing the document
              to 504 px wide. Root cause: parent had `justify-between`
              while the middle child (menu button) had `ml-auto` and
              ThemeToggle was a third direct flex sibling. Safari
              double-allocated the leftover space — justify-between's
              gap AND the auto-margin — pushing the last sibling 76 px
              past the viewport, which made iOS grow the layout
              viewport itself.
              Per the user's recommendation we now: drop
              `justify-between`, anchor the right-side controls in a
              single wrapper with `ml-auto`, and keep `flex items-center
              gap-1` inside. No child uses `ml-auto`, so Safari has only
              one place to allocate the free space. */}
          <div className="ml-auto flex items-center gap-1 shrink-0">
            <button
              className="p-2 rounded-lg hover:bg-accent"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Meny"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <ThemeToggle />
          </div>
        </div>
        {open && (
          <nav className="px-4 pb-4 space-y-1 border-t border-border pt-3 bg-background">
            {isPlatformAdmin && (
              <div className="mb-2">
                <BusinessSwitcher />
              </div>
            )}
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={linkClass}
                data-testid={n.testid}
              >
                <n.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{n.label}</span>
                {n.badge && renderBadge(n.badge)}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => { setOpen(false); setSupportOpen(true); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground/70 hover:bg-accent w-full text-left"
              data-testid="support-link-mobile"
            >
              <LifeBuoy className="w-4 h-4" strokeWidth={1.5} /> Trenger du hjelp?
            </button>
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground/70 hover:bg-accent w-full"
              data-testid="logout-mobile"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logg ut
            </button>
          </nav>
        )}
      </header>

      <div className="lg:flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 border-r border-border bg-background">
          <div className="px-4 h-16 flex items-center justify-between border-b border-border">
            <Link to="/dashboard" className="flex items-center pl-2" aria-label="ClientFlow">
              <Logo className="h-7 w-auto" />
            </Link>
            <ThemeToggle />
          </div>
          {isPlatformAdmin && (
            <div className="px-4 pt-4">
              <BusinessSwitcher />
            </div>
          )}
          <nav className="flex-1 p-4 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`desktop-${n.testid}`}>
                <n.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{n.label}</span>
                {n.badge && renderBadge(n.badge)}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-border">
            <button
              type="button"
              onClick={() => setSupportOpen(true)}
              className="mb-3 w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-foreground/70 hover:bg-accent hover:text-foreground transition-colors"
              data-testid="support-link-desktop"
            >
              <LifeBuoy className="w-4 h-4" strokeWidth={1.5} />
              <span className="flex-1 text-left">Trenger du hjelp?</span>
              <span className="text-[10px] text-muted-foreground">support</span>
            </button>
            <div className="mb-3 px-2 flex items-center gap-2">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-border" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-accent text-primary flex items-center justify-center text-xs font-semibold">
                  {(user?.name || "?").trim().split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
              <div className="text-xs min-w-0">
                <p className="font-semibold truncate" data-testid="user-name">{user?.name}</p>
                <p className="text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={() => { logout(); navigate("/"); }}
              data-testid="logout-desktop"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Logg ut
            </Button>
          </div>
        </aside>

        <main className="lg:pl-64 flex-1 min-w-0">
          <AdminImpersonationBanner />
          <div className="p-3 sm:p-6 lg:p-10 max-w-7xl mx-auto anim-in safe-px min-w-0 overflow-x-clip" key={typeof window !== "undefined" ? window.location.pathname : ""}>
            <Outlet />
          </div>
        </main>
      </div>
      <SupportDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        mailtoHref={supportHref}
      />
    </div>
  );
}
